import React, { useCallback } from 'react';
import { Upload, FileType, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/InfoTooltip';

export const FileUpload = ({ onFileSelect, selectedFile, onClear, accept = ".stl,.3mf,.gcode" }) => {
  const [dragActive, setDragActive] = React.useState(false);
  const inputRef = React.useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  }, [onFileSelect]);

  const handleChange = useCallback((e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  }, [onFileSelect]);

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  // Compact view when file is selected
  if (selectedFile) {
    return (
      <Card className="p-3 bg-white border-gray-200 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg bg-purple-100 flex-shrink-0">
              <FileType className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              data-testid="change-file-btn"
              size="sm"
              variant="outline"
              onClick={handleButtonClick}
              className="border-gray-300 bg-white hover:bg-gray-50 transition-all"
            >
              Change
            </Button>
            <Button
              data-testid="clear-file-btn"
              size="sm"
              variant="ghost"
              onClick={onClear}
              className="hover:bg-red-50 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          data-testid="file-input"
          className="hidden"
          accept={accept}
          onChange={handleChange}
        />
      </Card>
    );
  }

  // Full upload view when no file
  return (
    <Card
      data-testid="file-upload-area"
      className={`relative border-2 border-dashed transition-all shadow-sm ${
        dragActive
          ? 'border-purple-500 bg-purple-50 scale-[1.02]'
          : 'border-gray-300 bg-white hover:border-gray-400'
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {dragActive && (
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-purple-100 rounded-lg" />
      )}
      <div className="p-8 text-center relative">
        <input
          ref={inputRef}
          type="file"
          data-testid="file-input"
          className="hidden"
          accept={accept}
          onChange={handleChange}
        />

        <div className="flex flex-col items-center gap-3">
          <div className="p-4 rounded-2xl bg-gray-100 shadow-md">
            <Upload className="h-8 w-8 text-gray-400" />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-800">
              Drag & drop your 3D file here
            </p>
            <p className="text-xs text-gray-600">STL, 3MF, or G-code supported</p>
            <Button
              data-testid="browse-files-btn"
              size="sm"
              onClick={handleButtonClick}
              className="mt-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 shadow-lg shadow-purple-500/30 transition-all hover:scale-105 text-white"
            >
              Browse Files
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};